import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { FoldVertical, UnfoldVertical } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  type ExpandState,
  collectContainerPaths,
  kindOf,
  walkVisible,
} from './jsonTreeNavigation';
import { JsonTreeNode, type TreeApi, type TreeEditApi } from './JsonTreeNode';
import { renderRootPrimitive } from './JsonTreePrimitives';
import {
  type JsonEditChangeKind,
  type JsonValue,
  addArrayElement,
  addObjectKey,
  removeAtPath,
  setLeafAtPath,
} from './jsonTreeEdit';

export type JsonTreeViewProps = {
  value: unknown;
  /** Depth at which children become collapsed by default. 0 = root collapsed. */
  defaultExpandDepth?: number;
  /**
   * When provided, the tree becomes editable: primitives become click-to-edit,
   * containers expose `+ key` / `+ element` affordances, and each non-root
   * entry gets a remove button. The callback receives the full new root
   * value after every edit. The serialised form is the caller's
   * responsibility (`JSON.stringify` for the pending-value pipeline).
   */
  onChange?: (next: unknown) => void;
  /**
   * Optional analytics hook fired alongside `onChange` so callers can record
   * the *kind* of edit without re-deriving it from before/after diffing.
   */
  onEditTelemetry?: (kind: JsonEditChangeKind) => void;
};

// Default depth=2 expands the root container AND its immediate child
// containers (one level of nested objects/arrays) so a cell like
// `{ tags: ['a', 'b'] }` shows its array indices on first render, while
// still keeping the initial DOM bounded for deeply nested values.
// (Breadth at level 2 grows with top-level container sizes; the only
// caller using this default is the user-opened expanded-value dialog —
// all grid call sites pass an explicit defaultExpandDepth.)
export function JsonTreeView({
  value,
  defaultExpandDepth = 2,
  onChange,
  onEditTelemetry,
}: JsonTreeViewProps) {
  const rootKind = useMemo(() => kindOf(value), [value]);
  const isContainer = rootKind === 'object' || rootKind === 'array';

  // Lifted state: expand overrides + focused treeitem path. Lifted because
  // the keyboard handler at the tree root needs a global view of which
  // containers are open (to compute next/prev visible treeitem) and which
  // treeitem is currently focused.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(() => new Map());
  const expandState = useMemo<ExpandState>(
    () => ({ overrides, defaultExpandDepth }),
    [overrides, defaultExpandDepth],
  );
  const setExpanded = useCallback((path: string, expanded: boolean) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(path, expanded);
      return next;
    });
  }, []);

  // Container paths drive both the Expand-all/Collapse-all toolbar and the
  // decision to show it at all. Memoised on `value` so toggling overrides
  // doesn't re-walk the tree.
  const containerPaths = useMemo(() => collectContainerPaths(value), [value]);
  const hasExpandable = containerPaths.length > 0;
  const setAllExpanded = useCallback(
    (expanded: boolean) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        for (const p of containerPaths) next.set(p, expanded);
        return next;
      });
    },
    [containerPaths],
  );

  const [focusedPath, setFocusedPath] = useState<string | null>(
    isContainer ? 'root' : null,
  );
  const refs = useRef(new Map<string, HTMLDivElement>());
  const registerRef = useCallback((path: string, el: HTMLDivElement | null) => {
    if (el) refs.current.set(path, el);
    else refs.current.delete(path);
  }, []);

  // Imperatively focus the new path whenever focusedPath changes. Skip the
  // very first render so the tree doesn't steal focus on mount.
  const isFirstFocusEffect = useRef(true);
  useEffect(() => {
    if (isFirstFocusEffect.current) {
      isFirstFocusEffect.current = false;
      return;
    }
    if (!focusedPath) return;
    const el = refs.current.get(focusedPath);
    if (el && document.activeElement !== el) el.focus();
  }, [focusedPath]);

  const visible = useMemo(
    () => (isContainer ? walkVisible(value, expandState) : []),
    [value, expandState, isContainer],
  );

  // If a parent collapses out from under the focused treeitem (e.g. user
  // clicks a toggle button), re-anchor focus to the deepest visible
  // ancestor so keyboard nav has a valid starting point.
  useEffect(() => {
    if (!focusedPath || visible.length === 0) return;
    if (visible.some((v) => v.path === focusedPath)) return;
    let candidate = focusedPath;
    while (candidate.includes('.')) {
      candidate = candidate.substring(0, candidate.lastIndexOf('.'));
      if (visible.some((v) => v.path === candidate)) {
        setFocusedPath(candidate);
        return;
      }
    }
    setFocusedPath(visible[0].path);
  }, [visible, focusedPath]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Don't intercept keys when the user is typing inside an editable
      // descendant (inline leaf editor, key-name input, etc.). Without this
      // guard, the arrow-key tree-navigation would block caret movement.
      const target = e.target as HTMLElement | null;
      if (target && target !== e.currentTarget) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      }
      if (visible.length === 0) return;
      const currentPath = focusedPath ?? visible[0].path;
      const idx = visible.findIndex((v) => v.path === currentPath);
      if (idx < 0) return;
      const current = visible[idx];
      let nextPath: string | null = null;
      switch (e.key) {
        case 'ArrowDown':
          nextPath = visible[Math.min(idx + 1, visible.length - 1)].path;
          break;
        case 'ArrowUp':
          nextPath = visible[Math.max(idx - 1, 0)].path;
          break;
        case 'Home':
          nextPath = visible[0].path;
          break;
        case 'End':
          nextPath = visible[visible.length - 1].path;
          break;
        case 'ArrowRight':
          if (current.isContainer && !current.isEmpty) {
            if (!current.isExpanded) {
              setExpanded(current.path, true);
            } else {
              const child = visible[idx + 1];
              if (child && child.parentPath === current.path) {
                nextPath = child.path;
              }
            }
          }
          break;
        case 'ArrowLeft':
          if (current.isContainer && current.isExpanded) {
            setExpanded(current.path, false);
          } else if (current.parentPath !== null) {
            nextPath = current.parentPath;
          }
          break;
        default:
          return;
      }
      e.preventDefault();
      if (nextPath !== null && nextPath !== focusedPath) setFocusedPath(nextPath);
    },
    [visible, focusedPath, setExpanded],
  );

  // Edit API is bound to the *current* value so each operation reads from
  // and writes back through the parent's controlled value. Each method
  // fires telemetry inline so the apply + analytics happen atomically —
  // splitting them would close over the stale pre-apply value and revert
  // the edit on the second call.
  const editApi = useMemo<TreeEditApi | undefined>(() => {
    if (!onChange) return undefined;
    const apply = (next: unknown, kind: JsonEditChangeKind) => {
      onChange(next);
      onEditTelemetry?.(kind);
    };
    return {
      setLeaf: (path: string, v: JsonValue) =>
        apply(setLeafAtPath(value, path, v), 'leaf'),
      removeKey: (path: string) => apply(removeAtPath(value, path), 'remove_key'),
      removeElement: (path: string) =>
        apply(removeAtPath(value, path), 'remove_element'),
      addObjectKey: (parentPath: string, key: string) =>
        apply(addObjectKey(value, parentPath, key), 'add_key'),
      addArrayElement: (parentPath: string) =>
        apply(addArrayElement(value, parentPath), 'add_element'),
    };
  }, [value, onChange, onEditTelemetry]);

  if (!isContainer) {
    return (
      <div role="tree" aria-label="JSON value" className="font-mono text-xs p-2" data-testid="json-tree-root">
        {renderRootPrimitive(value)}
      </div>
    );
  }

  const api: TreeApi = {
    expandState,
    setExpanded,
    focusedPath,
    setFocusedPath,
    registerRef,
    edit: editApi,
  };

  return (
    <div className="relative h-full">
      <div
        role="tree"
        aria-label="JSON value"
        className="font-mono text-xs p-2 overflow-auto h-full"
        data-testid="json-tree-root"
        onKeyDown={handleKeyDown}
      >
        <JsonTreeNode label={null} value={value} depth={0} path="root" api={api} />
      </div>
      {hasExpandable && (
        <div
          className="absolute top-1 right-2 flex gap-0.5 z-10 pointer-events-none"
          data-testid="json-tree-toolbar"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground pointer-events-auto bg-background/70 backdrop-blur-[2px] hover:bg-background"
            onClick={() => setAllExpanded(true)}
            aria-label="Expand all"
            title="Expand all"
            data-testid="json-tree-expand-all"
          >
            <UnfoldVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground pointer-events-auto bg-background/70 backdrop-blur-[2px] hover:bg-background"
            onClick={() => setAllExpanded(false)}
            aria-label="Collapse all"
            title="Collapse all"
            data-testid="json-tree-collapse-all"
          >
            <FoldVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}
